// src/services/messageService.ts
import { Message, Conversation, MessageInput, TypingIndicator } from '../types/message';
// Removed unused imports: mockConversations, generateMockMessages
import { getApiBaseUrl, makeApiRequest } from '../utils/apiHelpers';

// API configuration - using helper for consistency
const API_BASE_URL = getApiBaseUrl();

export interface MessageServiceResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: any[];
}

class MessageService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Message Service initialized:', {
        baseURL: this.baseURL,
        environment: process.env.NODE_ENV
      });
    }
  }

  // Mock implementation - will be replaced with real API calls
  private async mockRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<MessageServiceResponse<T>> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 700));
    
    return {
      success: true,
      message: 'Mock response',
      data: {} as T
    };
  }

  // Fetch user profile by ID (fallback when participant data is missing)
  private async fetchUserProfile(userId: string): Promise<{ firstName?: string; lastName?: string; profilePicture?: string } | null> {
    console.log(`[fetchUserProfile] START - Fetching profile for userId: ${userId}`);
    try {
      const response = await makeApiRequest<any>(`/users/${userId}`, {
        method: 'GET',
      });

      console.log(`[fetchUserProfile] API response for ${userId}:`, {
        success: response.success,
        hasData: !!response.data,
        hasUser: !!response.data?.user,
        userStructure: response.data?.user ? Object.keys(response.data.user) : null,
      });

      if (response.success && response.data?.user) {
        const user = response.data.user;
        console.log(`[fetchUserProfile] User object structure for ${userId}:`, {
          hasPersonalInfo: !!user.personalInfo,
          personalInfoKeys: user.personalInfo ? Object.keys(user.personalInfo) : null,
          hasAccount: !!user.account,
          accountKeys: user.account ? Object.keys(user.account) : null,
          hasMetadata: !!user.metadata,
          rootKeys: Object.keys(user).filter(k => !['personalInfo', 'account', 'metadata'].includes(k)),
        });
        
        // Extract firstName and lastName from personalInfo
        const firstName = user.personalInfo?.firstName;
        const lastName = user.personalInfo?.lastName;
        
        console.log(`[fetchUserProfile] Extracted from personalInfo for ${userId}:`, {
          firstName,
          lastName,
          fullPersonalInfo: user.personalInfo,
        });
        
        // Try multiple paths for profilePicture (it might be in different locations)
        const profilePicture = user.profilePicture 
          || user.account?.profilePicture 
          || user.metadata?.profilePicture
          || undefined;
        
        console.log(`[fetchUserProfile] Profile picture search for ${userId}:`, {
          userProfilePicture: user.profilePicture,
          accountProfilePicture: user.account?.profilePicture,
          metadataProfilePicture: user.metadata?.profilePicture,
          final: profilePicture,
        });
        
        // If we got at least firstName, return the data
        if (firstName) {
          const result = {
            firstName,
            lastName,
            profilePicture,
          };
          console.log(`[fetchUserProfile] SUCCESS for ${userId}:`, result);
          return result;
        } else {
          console.warn(`[fetchUserProfile] No firstName found for ${userId}`);
        }
      } else {
        console.warn(`[fetchUserProfile] Invalid response for ${userId}:`, {
          success: response.success,
          hasData: !!response.data,
          hasUser: !!response.data?.user,
        });
      }
      return null;
    } catch (error) {
      console.error(`[fetchUserProfile] ERROR fetching user ${userId}:`, error);
      return null;
    }
  }

  // Get all conversations for current user
  async getConversations(): Promise<MessageServiceResponse<Conversation[]>> {
    console.log('[getConversations] START - Fetching conversations from API');
    try {
      const response = await makeApiRequest<Conversation[]>('/conversations', {
        method: 'GET',
      });

      console.log('[getConversations] API Response:', {
        success: response.success,
        hasData: !!response.data,
        dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
        dataLength: Array.isArray(response.data) ? response.data.length : null,
      });

      if (!response.success) {
        console.error('[getConversations] API call failed:', response.message);
        return {
          success: false,
          message: response.message || 'Failed to retrieve conversations',
          errors: response.errors || [],
        };
      }

      // Handle empty array case (successful but no conversations)
      if (!response.data || !Array.isArray(response.data)) {
        console.log('[getConversations] No conversations or invalid data format');
        return {
          success: true,
          message: 'No conversations found',
          data: [],
        };
      }

      // Log raw backend response structure
      if (response.data.length > 0) {
        const sample = response.data[0] as any;
        console.log('[getConversations] Sample raw backend conversation:', {
          id: sample._id?.toString() || sample.id,
          participantsCount: sample.participants?.length,
          participants: sample.participants?.map((p: any, idx: number) => ({
            index: idx,
            id: p._id?.toString() || p?.toString() || p,
            type: typeof p,
            isPopulated: p && typeof p === 'object' && p._id,
            firstName: p?.personalInfo?.firstName,
            lastName: p?.personalInfo?.lastName,
            profilePicture: p?.account?.profilePicture,
            allKeys: p && typeof p === 'object' ? Object.keys(p) : null,
          })),
        });
      }

      // Map backend format to frontend format
      // Backend now provides complete participant data (firstName, lastName, username) via Phase 1
      // profilePicture comes from matchUserData which is handled in mapConversations
      console.log('[getConversations] Mapping conversations...');
      const conversations = this.mapConversations(response.data);
      
      console.log('[getConversations] After mapping:', {
        count: conversations.length,
        sample: conversations.length > 0 ? {
          id: conversations[0].id,
          participantId: conversations[0].participant.id,
          participantName: `${conversations[0].participant.firstName} ${conversations[0].participant.lastName}`,
          hasProfilePicture: !!conversations[0].participant.profilePicture,
        } : null,
      });
      
      // Phase 2: Enrichment removed - backend now provides complete data
      // No need for additional API calls to /users/${userId}

      return {
        success: true,
        message: 'Conversations retrieved successfully',
        data: conversations,
      };
    } catch (error) {
      console.error('[getConversations] ERROR:', error);
      return {
        success: false,
        message: 'Failed to retrieve conversations',
        errors: [error],
      };
    }
  }

  // Map backend conversation format to frontend format
  private mapConversations(backendConversations: any[]): Conversation[] {
    if (!Array.isArray(backendConversations)) {
      return [];
    }

    // Get current user ID from localStorage
    const currentUserId = this.getCurrentUserId();
    // Normalize current user ID for comparison (remove any whitespace, convert to string)
    const normalizedCurrentUserId = currentUserId?.toString().trim().toLowerCase() || '';

    console.log('[mapConversations] ===== START MAPPING =====');
    console.log('[mapConversations] Current user ID:', currentUserId);
    console.log('[mapConversations] Normalized current user ID:', normalizedCurrentUserId);
    console.log('[mapConversations] Total conversations to map:', backendConversations.length);

    return backendConversations.map((conv, convIndex) => {
      console.log(`\n[mapConversations] ===== Conversation ${convIndex + 1}/${backendConversations.length} =====`);
      console.log('[mapConversations] Conversation ID:', conv._id || conv.id);
      
      // Get the other participant (not the current user)
      const participants = conv.participants || [];
      let otherParticipant: any = null;
      let otherParticipantId = '';

      console.log('[mapConversations] Participants array:', participants);
      console.log('[mapConversations] Participants count:', participants.length);

      // Find the other participant (could be an object if populated, or just an ID)
      if (Array.isArray(participants) && participants.length > 0) {
        // First pass: find the other participant (not current user)
        for (let i = 0; i < participants.length; i++) {
          const p = participants[i];
          
          // Skip null participants (Mongoose populate returns null for non-existent users)
          if (!p) {
            console.log(`[mapConversations] Participant ${i + 1} - NULL (user doesn't exist in DB - demo user)`);
            continue;
          }
          
          console.log(`[mapConversations] Processing participant ${i + 1}:`, {
            type: typeof p,
            isObject: typeof p === 'object',
            has_id: p && typeof p === 'object' && !!p._id,
            isUnpopulated: p && typeof p === 'object' && p._unpopulated === true,
            raw: p,
          });
          
          // Extract participant ID - handle both populated objects, unpopulated objects, and plain IDs
          let pId = '';
          if (p && typeof p === 'object' && p._id) {
            // If populated or unpopulated, get ID from _id field
            pId = p._id.toString();
            const isUnpopulated = p._unpopulated === true;
            console.log(`[mapConversations] Participant ${i + 1} - Extracted ID from _id:`, pId, isUnpopulated ? '(UNPOPULATED - demo user)' : '(populated)');
          } else if (p && typeof p === 'object' && p.id) {
            // Alternative ID field
            pId = p.id.toString();
            console.log(`[mapConversations] Participant ${i + 1} - Extracted ID from id:`, pId);
          } else if (p) {
            // If plain ID (ObjectId or string), convert to string
            pId = p.toString();
            console.log(`[mapConversations] Participant ${i + 1} - Extracted ID from toString:`, pId);
          }
          
          if (!pId) {
            console.log(`[mapConversations] Participant ${i + 1} - No ID found, skipping`);
            continue; // Skip if no ID found
          }
          
          // Normalize for comparison
          const normalizedPId = pId.trim().toLowerCase();
          
          console.log(`[mapConversations] Participant ${i + 1} - ID Comparison:`, {
            participantId: pId,
            normalizedParticipantId: normalizedPId,
            currentUserId: currentUserId,
            normalizedCurrentUserId: normalizedCurrentUserId,
            isCurrentUser: normalizedPId === normalizedCurrentUserId,
          });
          
          // Skip if this is the current user
          if (normalizedPId && normalizedPId === normalizedCurrentUserId) {
            console.log(`[mapConversations] Participant ${i + 1} - IS CURRENT USER, skipping`);
            console.log(`[mapConversations] Participant ${i + 1} - Current user details:`, {
              firstName: p?.personalInfo?.firstName,
              lastName: p?.personalInfo?.lastName,
            });
            continue; // Skip current user, look for the other participant
          }
          
          // Found the other participant
          console.log(`[mapConversations] Participant ${i + 1} - IS OTHER PARTICIPANT`);
          otherParticipantId = pId; // Keep original format
          
          // Check if this is an unpopulated participant (demo user)
          const isUnpopulated = p && typeof p === 'object' && p._unpopulated === true;
          
          if (isUnpopulated) {
            console.log(`[mapConversations] Participant ${i + 1} - UNPOPULATED (demo user - no data in User DB)`);
            // Store the participant object even if unpopulated - we'll use enrichment or match data
            otherParticipant = p;
          } else if (p && typeof p === 'object' && (p.personalInfo || p.account || p._id)) {
            // Populated participant with user details
            otherParticipant = p;
            console.log(`[mapConversations] Participant ${i + 1} - Other participant details:`, {
              id: pId,
              firstName: p.personalInfo?.firstName,
              lastName: p.personalInfo?.lastName,
              hasPersonalInfo: !!p.personalInfo,
              hasAccount: !!p.account,
            });
          } else {
            console.log(`[mapConversations] Participant ${i + 1} - Other participant ID only (not populated):`, pId);
          }
          break; // Found the other participant, stop looking
        }
        
        // Fallback: If we didn't find the other participant, use the first one that's not the current user
        // This handles edge cases where current user ID might not match exactly
        if (!otherParticipantId && participants.length > 0) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[mapConversations] No other participant found in first pass, trying fallback. Participants:', participants.length);
          }
          
          // If only one participant and it's not the current user, use it
          if (participants.length === 1) {
            const p = participants[0];
            let pId = '';
            if (p && typeof p === 'object' && p._id) {
              pId = p._id.toString();
            } else if (p && typeof p === 'object' && p.id) {
              pId = p.id.toString();
            } else if (p) {
              pId = p.toString();
            }
            if (pId) {
              otherParticipantId = pId;
              if (p && typeof p === 'object') {
                otherParticipant = p;
              }
            }
          } else {
            // Multiple participants: find one that's not current user
            for (const p of participants) {
              let pId = '';
              if (p && typeof p === 'object' && p._id) {
                pId = p._id.toString();
              } else if (p && typeof p === 'object' && p.id) {
                pId = p.id.toString();
              } else if (p) {
                pId = p.toString();
              }
              if (!pId) continue;
              
              const normalizedPId = pId.trim().toLowerCase();
              if (normalizedPId && normalizedPId !== normalizedCurrentUserId) {
                otherParticipantId = pId;
                if (p && typeof p === 'object') {
                  otherParticipant = p;
                }
                break;
              }
            }
          }
        }
        
        // Final check: If still no other participant found, this is a data issue
        // DO NOT use the current user as fallback - that would show wrong name
        if (!otherParticipantId && participants.length > 0) {
          // Check if all participants are the current user
          const allAreCurrentUser = participants.every(p => {
            let pId = '';
            if (p && typeof p === 'object' && p._id) {
              pId = p._id.toString();
            } else if (p && typeof p === 'object' && p.id) {
              pId = p.id.toString();
            } else if (p) {
              pId = p.toString();
            }
            if (!pId) return false;
            return pId.trim().toLowerCase() === normalizedCurrentUserId;
          });
          
          if (allAreCurrentUser) {
            console.error('[mapConversations] ❌ DATA ERROR: Conversation only has current user as participant. This is a backend data issue.');
            console.error('[mapConversations] ❌ Conversation ID:', conv._id || conv.id);
            console.error('[mapConversations] ❌ Participants:', participants.map(p => ({
              id: p && typeof p === 'object' ? (p._id?.toString() || p.id?.toString() || 'no-id') : p?.toString(),
              firstName: p?.personalInfo?.firstName,
              lastName: p?.personalInfo?.lastName,
            })));
            // Leave otherParticipantId empty - this will trigger "User" fallback which is better than showing current user's name
            // The enrichment logic cannot help here since we don't have the other participant's ID
          } else {
            console.warn('[mapConversations] ⚠️ NO OTHER PARTICIPANT FOUND - Current user ID might be incorrect:', currentUserId);
            console.warn('[mapConversations] ⚠️ All participants:', participants.map((p, idx) => ({
              index: idx,
              id: p && typeof p === 'object' ? (p._id?.toString() || p.id?.toString() || 'no-id') : p?.toString(),
              firstName: p?.personalInfo?.firstName,
              lastName: p?.personalInfo?.lastName,
            })));
            // Leave otherParticipantId empty - will show "User" fallback
          }
        }
      }
      
      console.log('[mapConversations] Final mapping result:', {
        conversationId: conv._id || conv.id,
        otherParticipantId,
        hasOtherParticipant: !!otherParticipant,
        firstName: otherParticipant?.personalInfo?.firstName || 'NOT FOUND',
        lastName: otherParticipant?.personalInfo?.lastName || 'NOT FOUND',
      });

      const participantState = conv.participantState || {};

      // Extract participant details from populated user object or use defaults
      // Note: If participant data is missing, we'll fetch it separately in getConversations()
      let firstName = otherParticipant?.personalInfo?.firstName;
      let lastName = otherParticipant?.personalInfo?.lastName;
      // Extract profilePicture from populated participant (Phase 4.1: Backend now includes personalInfo.profilePicture)
      let profilePicture = otherParticipant?.personalInfo?.profilePicture;
      
      // Check if this is an unpopulated participant (demo user)
      const isUnpopulated = otherParticipant && typeof otherParticipant === 'object' && otherParticipant._unpopulated === true;
      
      console.log('[mapConversations] Extracting participant details:', {
        hasOtherParticipant: !!otherParticipant,
        isUnpopulated,
        firstNameFromPersonalInfo: firstName,
        lastNameFromPersonalInfo: lastName,
        otherParticipantKeys: otherParticipant ? Object.keys(otherParticipant) : [],
      });
      
      // If participant is unpopulated (demo user), we need to use enrichment or match data
      if (isUnpopulated) {
        console.log('[mapConversations] Participant is UNPOPULATED (demo user) - will use enrichment or match data');
        // Leave firstName/lastName empty to trigger enrichment
        // Enrichment will try to fetch from match data or show "User" fallback
      } else if (otherParticipant && !firstName) {
        // If participant is populated but data is missing, check alternative paths
        console.log('[mapConversations] firstName not found in personalInfo, trying alternative paths');
        // Try alternative paths (in case backend structure differs)
        firstName = otherParticipant.firstName || otherParticipant.personalInfo?.firstName;
        lastName = otherParticipant.lastName || otherParticipant.personalInfo?.lastName;
        // profilePicture might be at root level if backend adds it
        profilePicture = otherParticipant.profilePicture;
        console.log('[mapConversations] Alternative paths result:', { firstName, lastName });
      }
      
      // Check for matchUserData from backend (for demo users or as fallback)
      if (conv.matchUserData && conv.matchUserData.participantId?.toString() === otherParticipantId) {
        console.log('[mapConversations] ✅ Found matchUserData for participant:', {
          participantId: conv.matchUserData.participantId,
          firstName: conv.matchUserData.firstName,
          lastName: conv.matchUserData.lastName,
          profilePicture: conv.matchUserData.profilePicture,
        });
        
        // Use matchUserData if available (overrides populated data if missing)
        if (conv.matchUserData.firstName && !firstName) {
          firstName = conv.matchUserData.firstName;
        }
        if (conv.matchUserData.lastName && !lastName) {
          lastName = conv.matchUserData.lastName;
        }
        // Use matchUserData.profilePicture as fallback if not found in populated participant
        if (conv.matchUserData.profilePicture && !profilePicture) {
          profilePicture = conv.matchUserData.profilePicture;
        }
      }
      
      // Use defaults only if we have no data at all
      // This will trigger enrichment if participantId exists
      if (!firstName) {
        firstName = 'User';
        if (isUnpopulated) {
          console.warn(`[mapConversations] ⚠️ Unpopulated participant (demo user) ${otherParticipantId}, using "User" fallback. Will trigger enrichment to fetch match data.`);
        } else {
          console.warn(`[mapConversations] ⚠️ No firstName found for participant ${otherParticipantId}, using "User" fallback. Will trigger enrichment.`);
        }
      }
      
      console.log('[mapConversations] Final participant data:', {
        id: otherParticipantId,
        firstName,
        lastName,
        profilePicture: profilePicture || 'undefined',
      });
      
      const isOnline = otherParticipant?.metadata?.isActive || false;

      return {
        id: conv._id?.toString() || conv.id || '',
        matchId: conv.matchId?.toString() || conv.matchId || '',
        participant: {
          id: otherParticipantId,
          firstName,
          lastName,
          profilePicture,
          isOnline,
        },
        lastMessage: conv.lastMessage
          ? {
              content: conv.lastMessage.contentPreview || '',
              timestamp: conv.lastMessage.createdAt || conv.updatedAt || new Date().toISOString(),
              senderId: conv.lastMessage.senderId?.toString() || '',
            }
          : undefined,
        unreadCount: participantState.unreadCount || 0,
        isActive: !participantState.archived,
        createdAt: conv.createdAt || new Date().toISOString(),
        updatedAt: conv.updatedAt || conv.createdAt || new Date().toISOString(),
      };
    });
  }

  // Get current user ID from localStorage
  private getCurrentUserId(): string {
    console.log('[getCurrentUserId] ===== START =====');
    try {
      // Method 1: Get from userData in localStorage
      const userData = localStorage.getItem('userData');
      console.log('[getCurrentUserId] userData from localStorage:', userData ? 'EXISTS' : 'NOT FOUND');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          console.log('[getCurrentUserId] Parsed userData:', {
            hasId: !!user.id,
            hasUserId: !!user.userId,
            id: user.id,
            userId: user.userId,
            allKeys: Object.keys(user),
          });
          if (user.id || user.userId) {
            const extractedId = (user.id || user.userId).toString();
            console.log('[getCurrentUserId] ✅ Extracted from userData:', extractedId);
            return extractedId;
          }
        } catch (parseError) {
          console.error('[getCurrentUserId] Error parsing userData:', parseError);
        }
      }
      
      // Method 2: Decode JWT token to get userId
      const token = localStorage.getItem('token');
      console.log('[getCurrentUserId] token from localStorage:', token ? 'EXISTS' : 'NOT FOUND');
      if (token) {
        try {
          const parts = token.split('.');
          console.log('[getCurrentUserId] Token parts count:', parts.length);
          if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[1]));
            console.log('[getCurrentUserId] Decoded JWT payload:', {
              hasUserId: !!payload.userId,
              hasId: !!payload.id,
              has_id: !!payload._id,
              userId: payload.userId,
              id: payload.id,
              _id: payload._id,
              allKeys: Object.keys(payload),
            });
            if (payload.userId || payload.id || payload._id) {
              const userId = (payload.userId || payload.id || payload._id).toString();
              console.log('[getCurrentUserId] ✅ Extracted from JWT token:', userId);
              return userId;
            }
          }
        } catch (e) {
          console.error('[getCurrentUserId] Error decoding JWT token:', e);
          // Token decode failed, continue to next method
        }
      }
    } catch (error) {
      console.error('[getCurrentUserId] Error:', error);
      console.warn('[getCurrentUserId] Failed to get user ID:', error);
    }
    console.warn('[getCurrentUserId] ⚠️ NO USER ID FOUND - returning empty string');
    console.log('[getCurrentUserId] ===== END =====');
    return '';
  }

  // Get messages for a specific conversation
  async getMessages(conversationId: string): Promise<MessageServiceResponse<Message[]>> {
    try {
      const response = await makeApiRequest<Message[]>(`/conversations/${conversationId}/messages`, {
        method: 'GET',
      });

      if (!response.success) {
        return {
          success: false,
          message: response.message || 'Failed to retrieve messages',
          errors: response.errors || [],
        };
      }

      // Handle empty array case
      if (!response.data || !Array.isArray(response.data)) {
        return {
          success: true,
          message: 'No messages found',
          data: [],
        };
      }

      // Map backend format to frontend format
      const messages = this.mapMessages(response.data);

      return {
        success: true,
        message: 'Messages retrieved successfully',
        data: messages,
      };
    } catch (error) {
      console.error('❌ Message Service Error:', error);
      return {
        success: false,
        message: 'Failed to retrieve messages',
        errors: [error],
      };
    }
  }

  // Map backend message format to frontend format
  private mapMessages(backendMessages: any[]): Message[] {
    if (!Array.isArray(backendMessages)) {
      return [];
    }

    return backendMessages.map((msg) => {
      const currentUserId = this.getCurrentUserId();
      const readReceipt = msg.status?.readBy?.find((r: any) => 
        r.userId?.toString() === currentUserId || r.userId === currentUserId
      );

      return {
        id: msg._id?.toString() || msg.id || '',
        conversationId: msg.conversationId?.toString() || msg.conversationId || '',
        senderId: msg.senderId?.toString() || msg.senderId || '',
        content: msg.content || '',
        timestamp: msg.createdAt || new Date().toISOString(),
        status: {
          sent: true, // Messages from backend are already sent
          delivered: msg.status?.deliveredTo?.length > 0 || false,
          read: !!readReceipt,
          readAt: readReceipt?.readAt || undefined,
        },
        type: msg.type || 'text',
        replyTo: msg.replyTo?.toString() || msg.replyTo || undefined,
      };
    });
  }

  // Send a new message
  async sendMessage(messageInput: MessageInput, conversationId: string): Promise<MessageServiceResponse<Message>> {
    try {
      const response = await makeApiRequest<Message>(`/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content: messageInput.content,
          type: messageInput.type || 'text',
          attachments: (messageInput as any).attachments || [],
          replyTo: messageInput.replyTo,
        }),
      });

      if (!response.success || !response.data) {
        return {
          success: false,
          message: response.message || 'Failed to send message',
          errors: response.errors || [],
        };
      }

      // Map backend format to frontend format
      const message = this.mapMessage(response.data);

      return {
        success: true,
        message: 'Message sent successfully',
        data: message,
      };
    } catch (error) {
      console.error('❌ Message Service Error:', error);
      return {
        success: false,
        message: 'Failed to send message',
        errors: [error],
      };
    }
  }

  // Map single backend message to frontend format
  private mapMessage(backendMessage: any): Message {
    const currentUserId = this.getCurrentUserId();
    const readReceipt = backendMessage.status?.readBy?.find((r: any) => 
      r.userId?.toString() === currentUserId || r.userId === currentUserId
    );

    return {
      id: backendMessage._id?.toString() || backendMessage.id || '',
      conversationId: backendMessage.conversationId?.toString() || backendMessage.conversationId || '',
      senderId: backendMessage.senderId?.toString() || backendMessage.senderId || '',
      content: backendMessage.content || '',
      timestamp: backendMessage.createdAt || new Date().toISOString(),
      status: {
        sent: true,
        delivered: backendMessage.status?.deliveredTo?.length > 0 || false,
        read: !!readReceipt,
        readAt: readReceipt?.readAt || undefined,
      },
      type: backendMessage.type || 'text',
      replyTo: backendMessage.replyTo?.toString() || backendMessage.replyTo || undefined,
    };
  }

  // Create or find conversation with a participant
  async createConversation(participantId: string, matchId?: string, matchUserData?: { firstName?: string; lastName?: string; profilePicture?: string }): Promise<MessageServiceResponse<Conversation>> {
    try {
      // Prepare request body with matchUserData if provided
      const requestBody: any = {
        participantId,
        matchId,
      };
      
      // Include matchUserData in request if provided
      if (matchUserData && (matchUserData.firstName || matchUserData.lastName || matchUserData.profilePicture)) {
        requestBody.matchUserData = {
          participantId, // Ensure participantId is included
          firstName: matchUserData.firstName,
          lastName: matchUserData.lastName,
          profilePicture: matchUserData.profilePicture,
        };
      }
      
      const response = await makeApiRequest<{ conversation: any; participantState: any }>('/conversations', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!response.success || !response.data) {
        return {
          success: false,
          message: response.message || 'Failed to create conversation',
          errors: response.errors || [],
        };
      }

      // Map backend format to frontend format
      let conversation = this.mapConversations([response.data.conversation])[0];
      
      // Always use matchUserData if provided (overrides any mapped data)
      // This ensures correct display even if mapping picks wrong participant initially
      if (matchUserData && conversation && (matchUserData.firstName || matchUserData.lastName || matchUserData.profilePicture)) {
        conversation = {
          ...conversation,
          participant: {
            ...conversation.participant,
            firstName: matchUserData.firstName || conversation.participant.firstName,
            lastName: matchUserData.lastName || conversation.participant.lastName,
            profilePicture: matchUserData.profilePicture || conversation.participant.profilePicture,
          },
        };
        console.log('[createConversation] Applied matchUserData to conversation:', {
          conversationId: conversation.id,
          participantId: conversation.participant.id,
          firstName: conversation.participant.firstName,
          lastName: conversation.participant.lastName,
          profilePicture: conversation.participant.profilePicture,
        });
      }

      return {
        success: true,
        message: 'Conversation created successfully',
        data: conversation,
      };
    } catch (error) {
      console.error('❌ Message Service Error:', error);
      return {
        success: false,
        message: 'Failed to create conversation',
        errors: [error],
      };
    }
  }

  // Mark messages as read
  async markAsRead(conversationId: string, messageIds: string[]): Promise<MessageServiceResponse<any>> {
    try {
      // Backend expects one messageId at a time, so mark the first one
      // TODO: Backend could be enhanced to accept multiple messageIds
      if (!messageIds || messageIds.length === 0) {
        return {
          success: false,
          message: 'No message IDs provided',
          errors: [],
        };
      }

      // Mark the first message as read (or most recent)
      const messageId = messageIds[0];
      const response = await makeApiRequest(`/messages/${messageId}/read`, {
        method: 'PUT',
        body: JSON.stringify({
          conversationId,
        }),
      });

      if (!response.success) {
        return {
          success: false,
          message: response.message || 'Failed to mark message as read',
          errors: response.errors || [],
        };
      }

      return {
        success: true,
        message: 'Message marked as read',
        data: { conversationId, messageId },
      };
    } catch (error) {
      console.error('❌ Message Service Error:', error);
      return {
        success: false,
        message: 'Failed to mark messages as read',
        errors: [error],
      };
    }
  }

  // Send typing indicator
  async sendTypingIndicator(typingIndicator: TypingIndicator): Promise<MessageServiceResponse<any>> {
    try {
      // Simulate API call
      await this.mockRequest('messages/typing', {
        method: 'POST',
        body: JSON.stringify(typingIndicator)
      });

      return {
        success: true,
        message: 'Typing indicator sent',
        data: typingIndicator
      };
    } catch (error) {
      console.error('❌ Message Service Error:', error);
      return {
        success: false,
        message: 'Failed to send typing indicator',
        errors: [error]
      };
    }
  }

  // Legacy method - kept for backward compatibility but should use createConversation(participantId, matchId?)
  // This method is deprecated and will be removed in future versions
  async createConversationLegacy(matchId: string): Promise<MessageServiceResponse<Conversation>> {
    try {
      // Simulate API call
      await this.mockRequest('conversations/create', {
        method: 'POST',
        body: JSON.stringify({ matchId })
      });

      // Create mock conversation
      const newConversation: Conversation = {
        id: `conv-${Date.now()}`,
        matchId,
        participant: {
          id: matchId.replace('match-', ''),
          firstName: 'New',
          lastName: 'Match',
          profilePicture: 'https://i.pravatar.cc/150?img=1',
          isOnline: true
        },
        unreadCount: 0,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      return {
        success: true,
        message: 'Conversation created successfully',
        data: newConversation
      };
    } catch (error) {
      console.error('❌ Message Service Error:', error);
      return {
        success: false,
        message: 'Failed to create conversation',
        errors: [error]
      };
    }
  }
}

// Create singleton instance
const messageService = new MessageService();

// Export service methods
export const messageAPI = {
  getConversations: () => messageService.getConversations(),
  getMessages: (conversationId: string) => messageService.getMessages(conversationId),
  sendMessage: (messageInput: MessageInput, conversationId: string) => 
    messageService.sendMessage(messageInput, conversationId),
  markAsRead: (conversationId: string, messageIds: string[]) => 
    messageService.markAsRead(conversationId, messageIds),
  sendTypingIndicator: (typingIndicator: TypingIndicator) => 
    messageService.sendTypingIndicator(typingIndicator),
  createConversation: (participantId: string, matchId?: string, matchUserData?: { firstName?: string; lastName?: string; profilePicture?: string }) => 
    messageService.createConversation(participantId, matchId, matchUserData)
};

export default messageService;